import { Viewer } from '@/components/editor/Viewer';
import { Editor, EditorRef } from '@/components/editor/Editor';
import { Button } from '@/components/ui/button';
import { AddMessageTicketEvent } from '@certhon/models';
import { useRef, useState, useEffect, KeyboardEventHandler } from 'react';

interface MessageContentProps {
  event: AddMessageTicketEvent;
  isEditing?: boolean;
  editingMessage?: string;
  onUpdateMessage?: (eventId: string, message: string) => void;
  onCancelEdit?: () => void;
}

interface MessageEditorProps {
  event: AddMessageTicketEvent;
  onUpdateMessage?: (eventId: string, message: string) => void;
  onCancelEdit?: () => void;
}

function MessageEditor({
  event,
  onUpdateMessage,
  onCancelEdit,
}: MessageEditorProps) {
  const editorRef = useRef<EditorRef>(null);
  const [message, setMessage] = useState(event.payload.message);

  useEffect(() => {
    if (editorRef.current) {
      // FIXME: message editor is not focussed when moving from editing one message directly to another
      editorRef.current?.focus();
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && onUpdateMessage) {
      onUpdateMessage(event.id, message.trim());
    }
  };

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      if (message.trim() && onUpdateMessage) {
        onUpdateMessage(event.id, message.trim());
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (onCancelEdit) {
        onCancelEdit();
      }
    }
  };

  return (
    <div
      className="focus-within:ring-ring/50 overflow-hidden rounded-lg border transition-colors
        focus-within:ring-[3px]"
    >
      <form onSubmit={handleSubmit}>
        <Editor
          key={event.id}
          ref={editorRef}
          namespace="message-edit"
          defaultValue={event.payload.message}
          onChange={setMessage}
          onKeyDown={handleKeyDown}
        />
      </form>
      <div className="bg-muted/50 flex items-center justify-between border-t px-3 py-2">
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => {
              if (message.trim() && onUpdateMessage) {
                onUpdateMessage(event.id, message.trim());
              }
            }}
            disabled={!message.trim()}
          >
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancelEdit}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

export function MessageContent({
  event,
  isEditing = false,
  onUpdateMessage,
  onCancelEdit,
}: MessageContentProps) {
  if (isEditing) {
    return (
      <MessageEditor
        event={event}
        onUpdateMessage={onUpdateMessage}
        onCancelEdit={onCancelEdit}
      />
    );
  }

  return <Viewer value={event.payload.message ?? ''} />;
}
